import { ApiError } from "../api/client";
import { consumeSetupSecret, consumeSimulationSecret, readSetupState } from "./api";
import type { SetupState } from "./schemas";

export type FragmentSecretKind = "setup" | "simulate";
export type FragmentBootstrapResult = {
  readonly kind: FragmentSecretKind | null;
  readonly status: "captured" | "resume" | "invalid" | "none";
};
export type FragmentExchangeError = {
  readonly status: number | null;
  readonly code: string | null;
};
export type FragmentExchangeResult =
  | { readonly kind: null; readonly status: "none" }
  | { readonly kind: FragmentSecretKind; readonly status: "invalid"; readonly error: FragmentExchangeError }
  | { readonly kind: FragmentSecretKind; readonly status: "error"; readonly error: FragmentExchangeError }
  | ({ readonly kind: "setup"; readonly status: "ready" } & SetupState)
  | {
      readonly kind: "simulate";
      readonly status: "complete";
      readonly effectiveAccountId: string;
      readonly expiresAt: string;
    };

type BrowserLocation = Pick<Location, "pathname" | "search" | "hash">;
type BrowserHistory = Pick<History, "state" | "replaceState">;
export type FragmentBootstrapBrowser = {
  readonly location: BrowserLocation;
  readonly history: BrowserHistory;
};

type PendingSecret = { readonly kind: FragmentSecretKind; readonly token: string };

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
let didBootstrap = false;
let bootstrapResult: FragmentBootstrapResult = { kind: null, status: "none" };
let pendingSecret: PendingSecret | null = null;
let exchangePromise: Promise<FragmentExchangeResult> | null = null;


export function bootstrapFragmentSecret(
  browser: FragmentBootstrapBrowser = window,
): FragmentBootstrapResult {
  if (didBootstrap) return bootstrapResult;
  didBootstrap = true;

  const { pathname, search, hash } = browser.location;
  const kind: FragmentSecretKind | null =
    pathname === "/setup" ? "setup" : pathname === "/simulate" ? "simulate" : null;
  if (kind === null) return bootstrapResult;

  if (hash.length > 0) {
    const token = hash.startsWith("#") ? hash.slice(1) : "";
    const isValid = search.length === 0 && TOKEN_PATTERN.test(token);
    browser.history.replaceState(null, "", pathname);
    if (!isValid) {
      bootstrapResult = { kind, status: "invalid" };
      return bootstrapResult;
    }
    pendingSecret = { kind, token };
    bootstrapResult = { kind, status: "captured" };
    return bootstrapResult;
  }

  bootstrapResult = kind === "setup" && search.length === 0
    ? { kind, status: "resume" }
    : { kind, status: "invalid" };
  return bootstrapResult;
}

async function exchangeCapturedSecret(): Promise<FragmentExchangeResult> {
  const { kind, status } = bootstrapResult;
  if (kind === null || status === "none") {
    return { kind: null, status: "none" };
  }
  if (status === "invalid") {
    return {
      kind,
      status: "invalid",
      error: { status: null, code: null },
    };
  }

  try {
    if (pendingSecret !== null) {
      const captured = pendingSecret;
      pendingSecret = null;
      if (captured.kind === "setup") {
        const state = await consumeSetupSecret(captured.token);
        return { kind: "setup", status: "ready", ...state };
      }
      const simulation = await consumeSimulationSecret(captured.token);
      return { kind: "simulate", status: "complete", ...simulation };
    }

    if (kind === "setup" && status === "resume") {
      const state = await readSetupState();
      return { kind: "setup", status: "ready", ...state };
    }
    return {
      kind,
      status: "invalid",
      error: { status: null, code: null },
    };
  } catch (error) {
    return {
      kind,
      status: "error",
      error: error instanceof ApiError
        ? { status: error.status, code: error.code }
        : { status: null, code: null },
    };
  }
}

export function startFragmentExchange(): Promise<FragmentExchangeResult> {
  if (!didBootstrap) bootstrapFragmentSecret();
  exchangePromise ??= exchangeCapturedSecret();
  return exchangePromise;
}
